
import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { parseCSV } from './utils/csvParser'
import { CSVUser, UploadResult } from './types/uploadTypes'
import UploadForm from './UploadForm'
import ValidationError from './ValidationError'
import UploadResults from './UploadResults'

const BulkUserUpload = () => {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [results, setResults] = useState<UploadResult[]>([])
  const [validationError, setValidationError] = useState<string | null>(null)
  const { toast } = useToast()

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    setValidationError(null)
    
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        toast({
          title: "Invalid file type",
          description: "Please upload a CSV file.",
          variant: "destructive",
        })
        return
      }
      setFile(selectedFile)
      setResults([])
    }
  }

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a CSV file to upload.",
        variant: "destructive",
      })
      return
    }

    setUploading(true)
    setProgress(10)
    setResults([])
    setValidationError(null)

    try {
      const text = await file.text()
      
      let users: CSVUser[]
      try {
        users = parseCSV(text)
        setProgress(30)
      } catch (error) {
        setValidationError(error.message)
        throw new Error(`CSV validation error: ${error.message}`)
      }

      setProgress(50)
      console.log(`Processing ${users.length} valid users for bulk creation`)

      const { data, error } = await supabase.functions.invoke('bulk-create-users', {
        body: { users }
      })

      if (error) throw error

      setResults(data.results)
      setProgress(100)
      
      const successCount = data.results.filter((r: any) => r.status === 'success').length
      toast({
        title: "Upload Complete",
        description: `Successfully created ${successCount} out of ${users.length} users.`,
        variant: successCount === users.length ? "default" : "destructive",
      })

    } catch (error) {
      console.error('Error uploading users:', error)
      toast({
        title: "Error uploading users",
        description: error.message,
        variant: "destructive",
      })
      setProgress(100)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bulk User Upload</CardTitle>
        <CardDescription>
          Upload a CSV file to create multiple users at once. The system can accept various CSV formats and will map fields automatically.
          Required fields: Name/Username or Email.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <UploadForm 
          handleFileChange={handleFileChange}
          handleUpload={handleUpload}
          file={file}
          uploading={uploading}
          progress={progress}
        />
        
        <ValidationError error={validationError} />
        
        <UploadResults results={results} />
      </CardContent>
    </Card>
  )
}

export default BulkUserUpload
